import React, { useState, useEffect } from 'react';
import {
  Plus,
  Settings,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Globe,
  Key,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Bell,
  Clock,
  Moon,
  Sun,
  ChevronDown,
  ChevronUp,
  Mail
} from 'lucide-react';
import { useSiteContext } from '../contexts/SiteContext';

const SiteManagement = ({ user }) => {
  const { refreshSites } = useSiteContext();
  const [sites, setSites] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    api_url: '',
    auth_type: 'token',
    consumer_key: '',
    consumer_secret: '',
    access_token: '',
    access_token_secret: '',
    api_key: '',
    api_secret: '',
    username: '',
    password: '',
    // Order monitoring settings
    order_monitoring_enabled: false,
    order_alert_email: '',
    order_alert_threshold_day: 2,
    order_alert_threshold_night: 6,
    order_night_start_hour: 22,
    order_night_end_hour: 8,
    order_site_timezone: 'UTC',
    downtime_alert_emails: ''
  });
  const [showMonitoringSettings, setShowMonitoringSettings] = useState(false);
  const [showCredentials, setShowCredentials] = useState({});
  const [errors, setErrors] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [assigningSite, setAssigningSite] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  useEffect(() => {
    fetchSites();
    if (user.role === 'admin') {
      fetchAllUsers();
    }
  }, [user]);

  const fetchAllUsers = async () => {
    try {
      const response = await fetch('/app/api/admin/users.php?role=site_manager', {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await fetch('/app/api/sites/list.php', {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSites(data.sites || []);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Site name is required';
    if (!formData.url) newErrors.url = 'Site URL is required';
    if (!formData.api_url) newErrors.api_url = 'API URL is required';
    if (!formData.api_key) newErrors.api_key = 'API Key is required';

    // Validate URLs
    if (formData.url && !isValidUrl(formData.url)) {
      newErrors.url = 'Please enter a valid URL';
    }
    if (formData.api_url && !isValidUrl(formData.api_url)) {
      newErrors.api_url = 'Please enter a valid API URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const endpoint = editingSite
        ? `/app/api/sites/update.php?id=${editingSite.id}`
        : '/app/api/sites/create.php';

      const response = await fetch(endpoint, {
        method: editingSite ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchSites();
        await refreshSites();
        resetForm();
      } else {
        setErrors({ submit: data.message || 'Failed to save site' });
      }
    } catch (error) {
      setErrors({ submit: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      url: site.url,
      api_url: site.api_url,
      auth_type: site.auth_type || 'token',
      consumer_key: site.consumer_key || '',
      consumer_secret: site.consumer_secret || '',
      access_token: site.access_token || '',
      access_token_secret: site.access_token_secret || '',
      api_key: site.api_key || '',
      api_secret: site.api_secret || '',
      username: site.username || '',
      password: '',
      // Order monitoring settings
      order_monitoring_enabled: site.order_monitoring_enabled === 1 || site.order_monitoring_enabled === '1' || site.order_monitoring_enabled === true,
      order_alert_email: site.order_alert_email || '',
      order_alert_threshold_day: site.order_alert_threshold_day || 2,
      order_alert_threshold_night: site.order_alert_threshold_night || 6,
      order_night_start_hour: site.order_night_start_hour ?? 22,
      order_night_end_hour: site.order_night_end_hour ?? 8,
      order_site_timezone: site.order_site_timezone || 'UTC',
      downtime_alert_emails: site.downtime_alert_emails || ''
    });
    setShowMonitoringSettings(site.order_monitoring_enabled === 1 || site.order_monitoring_enabled === '1' || site.order_monitoring_enabled === true);
    setShowAddForm(true);
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/app/api/sites/delete.php?id=${siteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchSites();
      }
    } catch (error) {
      console.error('Error deleting site:', error);
    }
  };

  const testConnection = async (siteId) => {
    try {
      const response = await fetch(`/app/api/sites/test.php?id=${siteId}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      // Update site status in local state
      setSites(prev => prev.map(site =>
        site.id === siteId
          ? { ...site, connection_status: data.success ? 'connected' : 'error', last_tested: new Date().toISOString() }
          : site
      ));
    } catch (error) {
      console.error('Error testing connection:', error);
    }
  };

  const handleAssignSite = (site) => {
    setAssigningSite(site);
    const assignedIds = (site.assigned_users || []).map(u => u.id);
    setSelectedUserIds(assignedIds);
  };

  const handleSaveAssignments = async () => {
    try {
      const response = await fetch('/app/api/sites/assign.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: assigningSite.id,
          user_ids: selectedUserIds
        })
      });

      if (response.ok) {
        await fetchSites();
        setAssigningSite(null);
        setSelectedUserIds([]);
      }
    } catch (error) {
      console.error('Error assigning site:', error);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      api_url: '',
      auth_type: 'token',
      consumer_key: '',
      consumer_secret: '',
      access_token: '',
      access_token_secret: '',
      api_key: '',
      api_secret: '',
      username: '',
      password: '',
      // Order monitoring settings
      order_monitoring_enabled: false,
      order_alert_email: '',
      order_alert_threshold_day: 2,
      order_alert_threshold_night: 6,
      order_night_start_hour: 22,
      order_night_end_hour: 8,
      order_site_timezone: 'UTC',
      downtime_alert_emails: ''
    });
    setEditingSite(null);
    setShowAddForm(false);
    setShowMonitoringSettings(false);
    setErrors({});
  };

  const toggleCredentialVisibility = (siteId, field) => {
    setShowCredentials(prev => ({
      ...prev,
      [`${siteId}_${field}`]: !prev[`${siteId}_${field}`]
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading && !showAddForm) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your Magento sites and their API connections
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Site
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">
              {editingSite ? 'Edit Site' : 'Add New Site'}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Site Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="My Magento Store"
                  value={formData.name}
                  onChange={handleInputChange}
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                  Site URL
                </label>
                <input
                  type="url"
                  name="url"
                  id="url"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="https://mystore.com"
                  value={formData.url}
                  onChange={handleInputChange}
                />
                {errors.url && <p className="mt-1 text-sm text-red-600">{errors.url}</p>}
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="api_url" className="block text-sm font-medium text-gray-700">
                  API URL
                </label>
                <input
                  type="url"
                  name="api_url"
                  id="api_url"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="https://mystore.com/rest/V1"
                  value={formData.api_url}
                  onChange={handleInputChange}
                />
                {errors.api_url && <p className="mt-1 text-sm text-red-600">{errors.api_url}</p>}
              </div>
            </div>

            {/* Auth Type Selection */}
            <div style={{ display: 'none' }}>
              <label htmlFor="auth_type" className="block text-sm font-medium text-gray-700 mb-2">
                Authentication Method
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer" style={{ display: 'none' }}>
                  <input
                    type="radio"
                    name="auth_type"
                    value="oauth"
                    checked={formData.auth_type === 'oauth'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    OAuth 1.0a (Integration)
                    <span className="block text-xs text-gray-500">Recommended - Consumer Key, Secret, Access Tokens</span>
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="auth_type"
                    value="token"
                    checked={true}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Admin Token
                    <span className="block text-xs text-gray-500">Legacy - Bearer token</span>
                  </span>
                </label>
              </div>
            </div>

            {/* OAuth Fields */}
            {formData.auth_type === 'oauth' && (
              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">OAuth 1.0a Credentials</h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="consumer_key" className="block text-sm font-medium text-gray-700">
                      Consumer Key
                    </label>
                    <input
                      type="text"
                      name="consumer_key"
                      id="consumer_key"
                      required={formData.auth_type === 'oauth'}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Your consumer key"
                      value={formData.consumer_key}
                      onChange={handleInputChange}
                    />
                    {errors.consumer_key && <p className="mt-1 text-sm text-red-600">{errors.consumer_key}</p>}
                  </div>

                  <div>
                    <label htmlFor="consumer_secret" className="block text-sm font-medium text-gray-700">
                      Consumer Secret
                    </label>
                    <input
                      type="password"
                      name="consumer_secret"
                      id="consumer_secret"
                      required={formData.auth_type === 'oauth'}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Your consumer secret"
                      value={formData.consumer_secret}
                      onChange={handleInputChange}
                    />
                    {errors.consumer_secret && <p className="mt-1 text-sm text-red-600">{errors.consumer_secret}</p>}
                  </div>

                  <div>
                    <label htmlFor="access_token" className="block text-sm font-medium text-gray-700">
                      Access Token
                    </label>
                    <input
                      type="text"
                      name="access_token"
                      id="access_token"
                      required={formData.auth_type === 'oauth'}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Your access token"
                      value={formData.access_token}
                      onChange={handleInputChange}
                    />
                    {errors.access_token && <p className="mt-1 text-sm text-red-600">{errors.access_token}</p>}
                  </div>

                  <div>
                    <label htmlFor="access_token_secret" className="block text-sm font-medium text-gray-700">
                      Access Token Secret
                    </label>
                    <input
                      type="password"
                      name="access_token_secret"
                      id="access_token_secret"
                      required={formData.auth_type === 'oauth'}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Your access token secret"
                      value={formData.access_token_secret}
                      onChange={handleInputChange}
                    />
                    {errors.access_token_secret && <p className="mt-1 text-sm text-red-600">{errors.access_token_secret}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Token Fields */}
            {formData.auth_type === 'token' && (
              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Admin Token Authentication</h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="api_key" className="block text-sm font-medium text-gray-700">
                      Admin Token / API Key
                    </label>
                    <input
                      type="text"
                      name="api_key"
                      id="api_key"
                      required={formData.auth_type === 'token'}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Your Magento admin token"
                      value={formData.api_key}
                      onChange={handleInputChange}
                    />
                {errors.api_key && <p className="mt-1 text-sm text-red-600">{errors.api_key}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Downtime Alert Emails */}
            <div className="border-t pt-6">
              <div>
                <label htmlFor="downtime_alert_emails" className="block text-sm font-medium text-gray-700">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Downtime Alert Email Addresses
                </label>
                <input
                  type="text"
                  name="downtime_alert_emails"
                  id="downtime_alert_emails"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="ops@example.com, dev@example.com"
                  value={formData.downtime_alert_emails}
                  onChange={handleInputChange}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Comma-separated list of emails to notify when this site goes down. Triggered via webhook: <code>/app/api/sites/downtime-alert.php?site_id={editingSite ? editingSite.id : '<id>'}</code>
                </p>
              </div>
            </div>

            {/* Order Monitoring Settings */}
            <div className="border-t pt-6">
              <button
                type="button"
                onClick={() => setShowMonitoringSettings(!showMonitoringSettings)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center">
                  <Bell className="h-5 w-5 text-gray-500 mr-2" />
                  <h3 className="text-sm font-medium text-gray-900">Order Monitoring</h3>
                  {formData.order_monitoring_enabled && (
                    <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      Enabled
                    </span>
                  )}
                </div>
                {showMonitoringSettings ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {showMonitoringSettings && (
                <div className="mt-4 space-y-4">
                  <p className="text-sm text-gray-500">
                    Get notified when your store hasn't received orders within a specified time period.
                    Different thresholds can be set for day and night hours.
                  </p>

                  {/* Enable Toggle */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="order_monitoring_enabled"
                      id="order_monitoring_enabled"
                      checked={formData.order_monitoring_enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, order_monitoring_enabled: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="order_monitoring_enabled" className="ml-2 block text-sm text-gray-700">
                      Enable order monitoring for this site
                    </label>
                  </div>

                  {formData.order_monitoring_enabled && (
                    <>
                      {/* Alert Email */}
                      <div>
                        <label htmlFor="order_alert_email" className="block text-sm font-medium text-gray-700">
                          <Mail className="h-4 w-4 inline mr-1" />
                          Alert Email Address
                        </label>
                        <input
                          type="email"
                          name="order_alert_email"
                          id="order_alert_email"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="alerts@example.com (leave empty to use account email)"
                          value={formData.order_alert_email}
                          onChange={handleInputChange}
                        />
                        <p className="mt-1 text-xs text-gray-500">Leave empty to send alerts to your account email</p>
                      </div>

                      {/* Day/Night Thresholds */}
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="order_alert_threshold_day" className="block text-sm font-medium text-gray-700">
                            <Sun className="h-4 w-4 inline mr-1 text-yellow-500" />
                            Daytime Threshold (hours)
                          </label>
                          <input
                            type="number"
                            name="order_alert_threshold_day"
                            id="order_alert_threshold_day"
                            min="1"
                            max="168"
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.order_alert_threshold_day}
                            onChange={handleInputChange}
                          />
                          <p className="mt-1 text-xs text-gray-500">Alert if no orders for this many hours during day</p>
                        </div>

                        <div>
                          <label htmlFor="order_alert_threshold_night" className="block text-sm font-medium text-gray-700">
                            <Moon className="h-4 w-4 inline mr-1 text-indigo-500" />
                            Nighttime Threshold (hours)
                          </label>
                          <input
                            type="number"
                            name="order_alert_threshold_night"
                            id="order_alert_threshold_night"
                            min="1"
                            max="168"
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.order_alert_threshold_night}
                            onChange={handleInputChange}
                          />
                          <p className="mt-1 text-xs text-gray-500">Alert if no orders for this many hours during night</p>
                        </div>
                      </div>

                      {/* Night Hours Configuration */}
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="order_night_start_hour" className="block text-sm font-medium text-gray-700">
                            <Clock className="h-4 w-4 inline mr-1" />
                            Night Starts At
                          </label>
                          <select
                            name="order_night_start_hour"
                            id="order_night_start_hour"
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.order_night_start_hour}
                            onChange={handleInputChange}
                          >
                            {[...Array(24)].map((_, i) => (
                              <option key={i} value={i}>
                                {i.toString().padStart(2, '0')}:00 ({i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label htmlFor="order_night_end_hour" className="block text-sm font-medium text-gray-700">
                            <Clock className="h-4 w-4 inline mr-1" />
                            Night Ends At
                          </label>
                          <select
                            name="order_night_end_hour"
                            id="order_night_end_hour"
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.order_night_end_hour}
                            onChange={handleInputChange}
                          >
                            {[...Array(24)].map((_, i) => (
                              <option key={i} value={i}>
                                {i.toString().padStart(2, '0')}:00 ({i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Timezone */}
                      <div>
                        <label htmlFor="order_site_timezone" className="block text-sm font-medium text-gray-700">
                          Site Timezone
                        </label>
                        <select
                          name="order_site_timezone"
                          id="order_site_timezone"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          value={formData.order_site_timezone}
                          onChange={handleInputChange}
                        >
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="Europe/London">London (GMT/BST)</option>
                          <option value="Europe/Paris">Paris (CET/CEST)</option>
                          <option value="Europe/Berlin">Berlin (CET/CEST)</option>
                          <option value="Asia/Dubai">Dubai (GST)</option>
                          <option value="Asia/Kolkata">India (IST)</option>
                          <option value="Asia/Singapore">Singapore (SGT)</option>
                          <option value="Asia/Tokyo">Tokyo (JST)</option>
                          <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">Used to determine day/night periods</p>
                      </div>

                      {/* Summary */}
                      <div className="mt-4 p-3 bg-blue-50 rounded-md">
                        <p className="text-sm text-blue-800">
                          <strong>Summary:</strong> You'll receive alerts if no orders are placed for{' '}
                          <strong>{formData.order_alert_threshold_day} hours</strong> during the day (
                          {formData.order_night_end_hour}:00 - {formData.order_night_start_hour}:00) or{' '}
                          <strong>{formData.order_alert_threshold_night} hours</strong> during the night (
                          {formData.order_night_start_hour}:00 - {formData.order_night_end_hour}:00).
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {errors.submit && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{errors.submit}</div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : (editingSite ? 'Update Site' : 'Add Site')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sites List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Your Sites ({sites.length})
          </h3>

          {sites.length > 0 ? (
            <div className="space-y-4">
              {sites.map((site) => (
                <div key={site.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <Globe className="h-8 w-8 text-gray-400" />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{site.name}</h4>
                        <p className="text-sm text-gray-500">{site.url}</p>
                        {user.role === 'admin' && site.owner_name && (
                          <p className="text-xs text-gray-500 mt-1">
                            Owner: {site.owner_name} ({site.owner_email})
                          </p>
                        )}
                        <div className="flex items-center space-x-2 mt-1">
                          {getStatusIcon(site.connection_status)}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(site.connection_status)}`}>
                            {site.connection_status || 'untested'}
                          </span>
                          {site.last_tested && (
                            <span className="text-xs text-gray-500">
                              Last tested: {new Date(site.last_tested).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {/* Order Monitoring Status */}
                        {(site.order_monitoring_enabled === 1 || site.order_monitoring_enabled === '1') && (
                          <div className="flex items-center space-x-2 mt-2">
                            <Bell className="h-4 w-4 text-blue-500" />
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              Order Monitoring Active
                            </span>
                            <span className="text-xs text-gray-500">
                              Day: {site.order_alert_threshold_day || 2}h / Night: {site.order_alert_threshold_night || 6}h
                            </span>
                            {site.order_last_order_at && (
                              <span className="text-xs text-gray-500">
                                | Last order: {new Date(site.order_last_order_at).toLocaleString()}
                              </span>
                            )}
                          </div>
                        )}
                        {user.role === 'admin' && site.assigned_users && site.assigned_users.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-gray-700">Assigned to:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {site.assigned_users.map(assignedUser => (
                                <span key={assignedUser.id} className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                  {assignedUser.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => testConnection(site.id)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        Test
                      </button>
                      {user.role === 'admin' && (
                        <button
                          onClick={() => handleAssignSite(site)}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <User className="h-4 w-4 mr-1" />
                          Assign
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(site)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(site.id)}
                        className="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* API Details (collapsed by default) */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">API URL:</span>
                        <span className="ml-2 text-gray-600">{site.api_url}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">API Key:</span>
                        <span className="ml-2 text-gray-600">
                          {showCredentials[`${site.id}_api_key`]
                            ? site.api_key
                            : '••••••••••••••••'
                          }
                        </span>
                        <button
                          onClick={() => toggleCredentialVisibility(site.id, 'api_key')}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                        >
                          {showCredentials[`${site.id}_api_key`] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Globe className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No sites added</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first Magento site.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Site
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Modal */}
      {assigningSite && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full m-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Assign Users to {assigningSite.name}
                </h3>
                <button
                  onClick={() => setAssigningSite(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Select site managers to assign to this site:
              </p>

              <div className="max-h-64 overflow-y-auto space-y-2">
                {allUsers.map(u => (
                  <label
                    key={u.id}
                    className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(u.id)}
                      onChange={() => toggleUserSelection(u.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{u.name}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setAssigningSite(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAssignments}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Save Assignments
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteManagement;